import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button } from "@mantine/core"
import { useMemo } from "react"
import { languageDisplayName } from "../../lib/language-display"
import classes from "./LanguageSwitchPrompt.module.css"

interface IProps {
  /** Language the transcript and summary are set to, as a BCP-47 code. */
  currentCode: string
  /** Language the recording sounds like, as a BCP-47 code. */
  candidateCode: string
  /** Switch the meeting to `candidateCode`. */
  onSwitch: () => void
  /** Keep `currentCode` for the rest of this recording. */
  onKeep: () => void
}

/**
 * Ask once whether to follow the language being spoken. Props in, events out.
 * The recording surface decides when to show it.
 */
export function LanguageSwitchPrompt({ currentCode, candidateCode, onSwitch, onKeep }: IProps): React.JSX.Element {
  const { i18n } = useLingui()

  const currentName = useMemo(() => languageDisplayName(currentCode, i18n.locale), [currentCode, i18n.locale])
  const candidateName = useMemo(() => languageDisplayName(candidateCode, i18n.locale), [candidateCode, i18n.locale])

  return (
    <div className={classes.root} data-testid="language-switch-prompt" role="status">
      <p className={classes.copy}>
        <Trans>
          This sounds like {candidateName}. The transcript and summary are set to {currentName}.
        </Trans>
      </p>
      <div className={classes.actions}>
        <Button type="button" color="brand" onClick={onSwitch}>
          <Trans>Switch to {candidateName}</Trans>
        </Button>
        <Button type="button" variant="subtle" color="brand" onClick={onKeep}>
          <Trans>Keep {currentName}</Trans>
        </Button>
      </div>
    </div>
  )
}
